import { IGroupsPlaceScope } from '../controllers/GroupsPlaceCtrl';
import { IGroupUsersSubplaceScope } from '../controllers/GroupUsersSubplaceCtrl';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { IGroupBasicInfo, IGroupInfo } from '../models/IGroupInfo';
import { ISiteUserInfo } from '../models/ISiteUserInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Groups } from '../resources/Groups';
import { IResult } from '../ServerApi/types/IResponse';
import { ISiteUser } from '../ServerApi/types/ISiteUser';
import { ActionHelpers } from './ActionHelpers';
import { IActionButton } from './ActionPanelService';
import { ActiveDirectoryGroupAction } from './ActiveDirectoryGroupAction';
import { AddUsersToGroupAction } from './AddUsersToGroupAction';
import { CreateGroupDialogAction } from './CreateGroupDialogAction';
import { DeleteGroupAction } from './DeleteGroupAction';
import { EditGroupMembershipAction } from './EditGroupMembershipAction';
import { RemoveUsersFromGroupAction } from './RemoveUsersFromGroupAction';
import { ServerService } from './ServerService';
import { SiteUsersActions } from './SiteUsersActions';
import { UpdateGroupNameAction } from './UpdateGroupNameAction';
import { UpdateGroupsMinimumSiteRoleAction } from './UpdateGroupsMinimumSiteRoleAction';

export let GroupUsersEvent = {
  usersRemoved: 'usersRemoved',
  usersAdded: 'usersAdded'
};

export class GroupActions {

  public static $inject = [
    'ActiveDirectoryGroupAction',
    'AddUsersToGroupAction',
    'CreateGroupDialogAction',
    'DeleteGroupAction',
    'UpdateGroupsMinimumSiteRoleAction',
    'EditGroupMembershipAction',
    'RemoveUsersFromGroupAction',
    'ServerService',
    'SiteUsersActions',
    'UpdateGroupNameAction'
  ];

  private groupsActionDefinition: IActionButton<IGroupInfo, {}>[];                // actions taken on groups
  private groupUsersActionDefinition: IActionButton<ISiteUserInfo, {}>[];         // actions taken on users within groups
  private allUsersGroupUsersActionDefinition: IActionButton<ISiteUserInfo, {}>[]; // actions taken on users within the All Users group

  constructor(
    private ActiveDirectoryGroupAction: ActiveDirectoryGroupAction,
    private AddUsersToGroupAction: AddUsersToGroupAction,
    private CreateGroupDialogAction: CreateGroupDialogAction,
    private DeleteGroupAction: DeleteGroupAction,
    private UpdateGroupsMinimumSiteRoleAction: UpdateGroupsMinimumSiteRoleAction,
    private EditGroupMembershipAction: EditGroupMembershipAction,
    private RemoveUsersFromGroupAction: RemoveUsersFromGroupAction,
    private serverService: ServerService,
    private SiteUsersActions: SiteUsersActions,
    private UpdateGroupNameAction: UpdateGroupNameAction
  ) {

    this.groupsActionDefinition = [
      this.syncGroupsAction(),
      this.updateGroupNameAction(),
      this.setGroupSiteRoleAction(),
      this.deleteGroupAction()
    ];

    this.groupUsersActionDefinition = [
      this.editUsersGroupMembershipAction(),
      this.SiteUsersActions.setSiteUsersSiteRoleAction(),
      this.removeUsersFromGroupAction()
    ];

    this.allUsersGroupUsersActionDefinition = [
      this.SiteUsersActions.setSiteUsersSiteRoleAction()
    ];

  }

  // public api

  public getGroupsActionButtons(): IActionButton<IGroupInfo, {}>[] {
    return this.groupsActionDefinition;
  }

  public getGroupUsersActionButtons(isAllUsersGroup: boolean): IActionButton<ISiteUserInfo, {}>[] {
    return isAllUsersGroup ? this.allUsersGroupUsersActionDefinition : this.groupUsersActionDefinition;
  }

  public createGroup(dialogScope: ng.IScope): ng.IPromise<any> {
    return this.CreateGroupDialogAction.showCreateGroupDialog(dialogScope);
  }

  public deleteGroup(group: IGroupBasicInfo, dialogScope: ng.IScope): ng.IPromise<any> {
    const groups = new Set<IGroupBasicInfo>((group: IGroupInfo) => group.id);
    groups.add(group);
    return this.DeleteGroupAction.showDeleteGroupDialog(groups, dialogScope);
  }

  public addUsersToGroup(group: IGroupInfo): ng.IPromise<any> {
    return this.AddUsersToGroupAction.showAddUsersToGroupDialog(group);
  }

  public renameGroup(group: IGroupInfo, dialogScope: ng.IScope): ng.IPromise<IResult> {
    return this.UpdateGroupNameAction.showUpdateGroupNameDialog(group, dialogScope);
  }

  public syncAdGroup(group: IGroupBasicInfo, dialogScope: ng.IScope): ng.IPromise<IResult> {
    return this.ActiveDirectoryGroupAction.showSyncAdGroupDialog(group, dialogScope.$new(true));
  }

  // action button factories

  private removeUsersFromGroupAction(): IActionButton<ISiteUserInfo, {}> {
    return {
      name: 'remove',
      translate: 'remove_action',
      isAvailable: (items: Set<ISiteUserInfo>) => items.size() > 0,
      execute: (items: Set<ISiteUserInfo>, context: IActionContext) => {
        const scope = <IGroupUsersSubplaceScope>context.scope;
        this.RemoveUsersFromGroupAction.showRemoveUsersFromGroupDialog(items, scope.group).then((result: IResult) => {
          if (result) {
            ActionHelpers.deselectAllExceptErrors(scope.selectedItems, result, CommonErrorCode.SiteUserNotFound);
            scope.refreshContent();
            scope.$emit(GroupUsersEvent.usersRemoved);
          }
        });
      }
    };
  }

  // There is an identical action definition in SiteUsersActions.
  // This one a bit different in that it clears selection in case some users get removed from
  // the group (deselectAllExceptErrors). That way we won't have hidden selection lurking around.
  // TODO change the action to use didActionChangeItemVisibility and then merge the definitions TFS 694617
  public editUsersGroupMembershipAction(): IActionButton<ISiteUserInfo, {}> {
    return {
      name: 'groupMembership',
      translate: 'groupMembership_action',
      isAvailable: (items: Set<ISiteUserInfo>): boolean => items.size() > 0,
      execute: (items: Set<ISiteUserInfo>, context: IActionContext) => {
        const scope = <IGroupUsersSubplaceScope>context.scope;
        this.EditGroupMembershipAction.showEditGroupMembershipDialog(items, context)
          .then((result: IResult) => {
            if (result) {
              ActionHelpers.deselectAllExceptErrors(scope.selectedItems, result, CommonErrorCode.SiteUserNotFound);
              scope.refreshContent();
            }
          });
      }
    };
  }

  public setGroupSiteRoleAction(): IActionButton<IGroupInfo, {}> {
    return {
      name: 'siteRole',
      translate: 'minimumSiteRole_action',
      isAvailable: (items: Set<IGroupInfo>) => items.size() > 0,
      isDisabled: (items: Set<IGroupInfo>) => items.size() === 1 && !this.canSetSiteRole(items.first()),
      execute: (items: Set<IGroupInfo>, context: IActionContext) => {
        const scope = <IGroupsPlaceScope>context.scope;
        this.UpdateGroupsMinimumSiteRoleAction.showUpdateGroupsMinimumSiteRoleDialog(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(scope.selectedItems, result, CommonErrorCode.GroupNotFound);
            scope.refreshContent();
          });
      }
    };
  }

  public deleteGroupAction(): IActionButton<IGroupInfo, {}> {
    return {
      name: 'delete',
      translate: 'delete_action',
      isAvailable: (items: Set<IGroupInfo>) => items.size() > 0,
      isDisabled: (items: Set<IGroupInfo>) => items.size() === 1 && !this.isDeletable(items.first()),
      execute: (items: Set<IGroupInfo>, context: IActionContext) => {
        const scope = <IGroupsPlaceScope>context.scope;
        this.DeleteGroupAction.showDeleteGroupDialog(items, scope).then((result: IResult) => {
          if (result) {
            ActionHelpers.deselectAllExceptErrors(scope.selectedItems, result, CommonErrorCode.GroupNotFound);
            scope.refreshContent();
          }
        });
      }
    };
  }

  public updateGroupNameAction(): IActionButton<IGroupInfo, {}> {
    return {
      name: 'rename',
      translate: 'rename_action',
      isAvailable: (items: Set<IGroupInfo>) => items.size() > 0,
      isDisabled: (items: Set<IGroupInfo>) => items.size() !== 1 || !this.isRenamable(items.first()),
      execute: (items: Set<IGroupInfo>, context: IActionContext) => {
        const scope = <IGroupsPlaceScope>context.scope;
        this.UpdateGroupNameAction.showUpdateGroupNameDialog(items.first(), scope).then((result: IResult) => {
          if (result) {
            ActionHelpers.deselectNotFound(scope.selectedItems, result, CommonErrorCode.GroupNotFound);
            scope.refreshContent();
          }
        });
      }
    };
  }

  public syncGroupsAction(): IActionButton<IGroupInfo, {}> {
    return {
      name: 'synchronize',
      translate: 'synchronize_action',
      isAvailable: (items: Set<IGroupInfo>) => items.size() > 0 && this.serverService.canSessionUserManageUsers(),
      isDisabled: (items: Set<IGroupInfo>) => items.size() === 1 && !this.isSyncable(items.first()),
      execute: (items: Set<IGroupInfo>, context: IActionContext) => {
        const scope = <IGroupsPlaceScope>context.scope;
        this.ActiveDirectoryGroupAction.showSyncAdGroupsDialog(items.toArray(), context.scope).then((result: IResult) => {
          if (result) {
            ActionHelpers.deselectNotFound(scope.selectedItems, result, CommonErrorCode.GroupNotFound);
            scope.refreshContent();
          }
        });
      }
    };
  }

   // returns true if the given group site role can be changed
  private canSetSiteRole(group: IGroupInfo): boolean {
    return this.isActiveDirectoryGroup(group) && this.serverService.canSessionUserManageUsers();
  }

  // returns true if the given group can be deleted
  private isDeletable(group: IGroupInfo): boolean {
    if (this.isActiveDirectoryGroup(group) && !this.serverService.canSessionUserManageUsers()) {
      return false;
    }
    return !group.isAllUsersGroup;
  }

  // returns true if the given group can be renamed
  private isRenamable(group: IGroupInfo): boolean {
    return !group.isAllUsersGroup && !this.isActiveDirectoryGroup(group);
  }

  // returns true if the given group can be synced from active directory
  private isSyncable(group: IGroupInfo): boolean {
    return !group.isAllUsersGroup && this.isActiveDirectoryGroup(group);
  }

  // returns true if the given group is not local
  private isActiveDirectoryGroup(group: IGroupInfo): boolean {
    return Groups.isActiveDirectory(group);
  }

}

Module.instance().service('GroupActions', GroupActions);
